import React, { useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import ValidUrl from "valid-url";

import Card from "../../../UI/Card/Card";
import Button from "../../../UI/Button/Button";
import ErrorPanel, { ErrorSize } from "../../../UI/ErrorPanel/ErrorPanel";
import CheckBox from "../../../UI/Button/CheckboxButton";

import { faEdit, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import * as ApplicationConstant from "../../../Constants/ApplicationConstant";
import * as ApplicationsUtils from "../../../Utils/ApplicationsUtils";
import * as ApplicationsActions from "../../../../middleware/Store/Actions/ApplicationsActions";
import * as SandBoxApplicationsActions from "../../../../middleware/Store/Actions/SandBoxApplicationsActions";
import * as ErrorUtils from "../../../Utils/ErrorUtils";

import LogService from "../../../../middleware/CommonSdk/LogService";

const LOG_TAG = "APP-EDITION-OAUTH";

const OAuth = (props) => {

    const intl = useIntl();
    const [redirectUris, setRedirectUris] = useState([]);
    const [termsOfServiceUrl, setTermsOfServiceUrl] = useState("");
    const [privacyPoliciesUrl, setPrivacyPoliciesUrl] = useState("");
    const [implicitGrant, setImplicitGrant] = useState(false);
    const [refreshTokenRotation, setRefreshTokenRotation] = useState(false);
    const [allowSeveralRefreshTokens, setAllowSeveralRefreshTokens] = useState(false);
    const [application, setApplication] = useState(null);
    const [progress, setProgress] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const redirectUrisInputRef = useRef();

    useEffect(() => {
        LogService.trace(LOG_TAG, "useEffect application");
        if (props.applicationId) {
            const foundApplication = ApplicationsUtils.getApplicationById(props.applicationId, props.applications);
            setApplication(foundApplication);
            if (foundApplication) {
                setRedirectUris(foundApplication.oauthRedirectUris ? foundApplication.oauthRedirectUris : []);
                setTermsOfServiceUrl(foundApplication.termsOfServiceUrl ? foundApplication.termsOfServiceUrl : "");
                setPrivacyPoliciesUrl(foundApplication.privacyPoliciesUrl ? foundApplication.privacyPoliciesUrl : "");
                setImplicitGrant(foundApplication.enableOAuthImplicitGrant === true);
                setRefreshTokenRotation(foundApplication.enableOAuthRefreshTokenRotation === true);
                setAllowSeveralRefreshTokens(foundApplication.enableOAuthAllowSeveralRefreshTokens === true);
            }

            if (props.applicationId !== application?.id) {
                setProgress(false);
                setErrorMessage("");
            }
        }
    }, [props.applications, props.applicationId]);

    const handleUpdate = (event) => {
        LogService.trace(LOG_TAG, "handleUpdate", event.target.value);
        const criteria = {
            oauthRedirectUris: redirectUris,
            termsOfServiceUrl: termsOfServiceUrl,
            privacyPoliciesUrl: privacyPoliciesUrl,
            enableOAuthImplicitGrant: implicitGrant,
            enableOAuthRefreshTokenRotation: refreshTokenRotation,
            enableOAuthAllowSeveralRefreshTokens: allowSeveralRefreshTokens
        };

        setProgress(true);
        props.update(application?.id, criteria)
            .then(() => {
                LogService.debug(LOG_TAG, "handleUpdate success");
                setProgress(false);
            })
            .catch((errorPayload) => {
                LogService.debug(LOG_TAG, "handleUpdate error");
                setProgress(false);
                setErrorMessage(ErrorUtils.getPrettyErrorMessage(errorPayload, intl));
            });
    };

    const handleImplicitGrantClick = (isChecked) => {
        setImplicitGrant(isChecked);
    }

    const handleRefreshTokenRotationClick = (isChecked) => {
        setRefreshTokenRotation(isChecked);
    }

    const handleAllowSeveralRefreshTokenClick = (isChecked) => {
        setAllowSeveralRefreshTokens(isChecked);
    }

    const handleTermsOfServiceUrlChange = (event) => {
        setTermsOfServiceUrl(event.target.value);
    }

    const handlePrivacyPoliciesUrlChange = (event) => {
        setPrivacyPoliciesUrl(event.target.value);
    }

    const handleRedirectUrisChange = () => {
        const isValid = redirectUrisInputRef.current.value.length === 0 || ValidUrl.isUri(redirectUrisInputRef.current.value) !== (null || undefined);
        if (isValid) {
            redirectUrisInputRef.current.classList.remove("is-invalid");
        } else {
            redirectUrisInputRef.current.classList.add("is-invalid");
        }
    }

    const handleRedirectUrisSubmit = (event) => {
        LogService.trace(LOG_TAG, "handleRedirectUrisSubmit");
        event.preventDefault();

        const isValid = ValidUrl.isUri(redirectUrisInputRef.current.value) !== (null || undefined);

        if (isValid && redirectUris.length < ApplicationConstant.OauthRedirectUris_MaxValue) {
            const newRedirectURis = [...redirectUris.filter(uri => uri !== redirectUrisInputRef.current.value), redirectUrisInputRef.current.value];
            setRedirectUris(newRedirectURis);
        } else {
            LogService.warn(LOG_TAG, "handleRedirectUrisSubmit max value reached. skipped");
        }
    }

    const handleRedirectUrisDelete = (value) => {
        const newRedirectURis = redirectUris.filter(uri => uri !== value);
        setRedirectUris(newRedirectURis);
    }

    LogService.trace(LOG_TAG, "render");
    const refreshTokenRotationMessage = intl.formatMessage({ id: "applicationEditionOAuthRefreshTokenRotationMessage" }, { b: chunks => <b>{chunks}</b> });
    const disableRedirectUrisInput = redirectUris.length >= ApplicationConstant.OauthRedirectUris_MaxValue;

    let redirectUriTableValues = redirectUris.map((value, i) => {
        return (
            <div key={i} className="input-group mb-1">
                <div className="form-control overlay" disabled >{value}</div>
                <div className="input-group-append">
                    <button id="idTrashButton" className="btn input-btn" type="button"
                        onClick={() => { handleRedirectUrisDelete(value) }}>
                        <FontAwesomeIcon icon={faTrashAlt} />
                    </button>
                </div>
            </div>
        );
    });

    return (
        <Card title={intl.messages.applicationEditionOAuthTitle}>
            <div className="form-group">
                <form onSubmit={handleRedirectUrisSubmit}>
                    <label className="input-label">{intl.messages.applicationEditionOAuthRedirectUrisTitle}</label>
                    <input
                        id="redirectUrisInputId"
                        ref={redirectUrisInputRef}
                        className="form-control"
                        placeholder={intl.messages.applicationEditionOAuthRedirectUrisPlaceholder}
                        //type="url"
                        autoComplete="off"
                        onChange={handleRedirectUrisChange}
                        disabled={!props.isUpdateEnabled || disableRedirectUrisInput}
                    />
                    <div id="redirectUrisTable" className="form-group mt-1">
                        {redirectUriTableValues}
                    </div>
                </form>
            </div>
            <div className="panel bg-fog mb-3">{intl.messages.applicationEditionOAuthRedirectUrisMessage}</div>

            <div className="form-group">
                <label className="input-label">{intl.messages.applicationEditionOAuthTermsOfServiceUrlTitle}</label>
                <input
                    id="termsOfServiceUrlId"
                    className="form-control"
                    value={termsOfServiceUrl}
                    placeholder={intl.messages.applicationEditionOAuthTermsOfServiceUrlTitle}
                    type="text"
                    autoComplete="off"
                    onChange={handleTermsOfServiceUrlChange}
                    disabled={!props.isUpdateEnabled}
                />
            </div>
            <div className="panel bg-fog mb-3">{intl.messages.applicationEditionOAuthTermsOfServiceUrlMessage}</div>

            <div className="form-group">
                <label className="input-label">{intl.messages.applicationEditionOAuthPrivacyPoliciesUrlTitle}</label>
                <input
                    id="privacyPoliciesUrlId"
                    className="form-control"
                    value={privacyPoliciesUrl}
                    placeholder={intl.messages.applicationEditionOAuthPrivacyPoliciesUrlTitle}
                    type="text"
                    autoComplete="off"
                    onChange={handlePrivacyPoliciesUrlChange}
                    disabled={!props.isUpdateEnabled}
                />
            </div>
            <div className="panel bg-fog mb-3">{intl.messages.applicationEditionOAuthPrivacyPoliciesUrlMessage}</div>

            <div className="form-group">
                <CheckBox
                    id="implicitGrantId"
                    label={intl.messages.applicationEditionOAuthImpicitGrantTitle}
                    checked={implicitGrant}
                    onClick={handleImplicitGrantClick}
                    disabled={!props.isUpdateEnabled} />
            </div>
            <div className="panel bg-fog mb-3">{intl.messages.applicationEditionOAuthImpicitGrantMessage}</div>

            <div className="form-group">
                <CheckBox
                    id="refreshTokenRotationId"
                    label={intl.messages.applicationEditionOAuthRefreshTokenRotationTitle}
                    checked={refreshTokenRotation}
                    onClick={handleRefreshTokenRotationClick}
                    disabled={!props.isUpdateEnabled} />
            </div>
            <div className="panel bg-fog mb-3">{refreshTokenRotationMessage}</div>

            <div className="form-group">
                <CheckBox
                    id="allowSeveralRefreshTokensId"
                    label={intl.messages.applicationEditionOAuthAllowSeveralRefreshTokenTitle}
                    checked={allowSeveralRefreshTokens}
                    onClick={handleAllowSeveralRefreshTokenClick}
                    disabled={!props.isUpdateEnabled} />
            </div>
            <div className="panel bg-fog mb-3">{intl.messages.applicationEditionOAuthAllowSeveralRefreshTokenMessage}</div>

            {props.isUpdateEnabled &&
                <div>
                    <Button
                        id="updateButton"
                        label={intl.messages.update}
                        icon={faEdit}
                        onClick={handleUpdate}
                        spinner={progress}
                        disabled={progress}
                        className="btn-submit"
                    />
                    {errorMessage &&
                        <div className="col-10 float-right">
                            <ErrorPanel size={ErrorSize.Small} >{errorMessage}</ErrorPanel>
                        </div>
                    }
                </div>
            }
        </Card >
    );
}

OAuth.propTypes = {
    applicationId: PropTypes.string,
    isUpdateEnabled: PropTypes.bool,
    isSandBoxApplication: PropTypes.bool
};

OAuth.defaultProps = {
    applicationId: "",
    isUpdateEnabled: false,
    isSandBoxApplication: false
}

const mapStateToProps = (state, props) => {
    return {
        applications: props.isSandBoxApplication ? state.sandBoxApplications.applications : state.applications.applications
    };
};

const mapDispatchToProps = (dispatch, props) => {
    return {
        update: (appId, criteria) => dispatch(props.isSandBoxApplication ? SandBoxApplicationsActions.updateApplication(appId, criteria) : ApplicationsActions.updateApplication(appId, criteria))
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(OAuth);